import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { FRACTION_SUB_OPS } from "../lib/constants";
import { ProfileBar } from "../components/ProfileBar";
import { PageShell } from "../components/PageShell";
import { ClayButton } from "../components/ClayButton";
import { FractionVisual } from "../components/FractionVisual";

const PREVIEW_FRACTIONS = {
  suma: { left: { numerator: 1, denominator: 4 }, right: { numerator: 2, denominator: 4 } },
  resta: { left: { numerator: 3, denominator: 5 }, right: { numerator: 1, denominator: 5 } },
  multiplicacion: { left: { numerator: 1, denominator: 2 }, right: { numerator: 1, denominator: 3 } },
  division: { left: { numerator: 1, denominator: 2 }, right: { numerator: 1, denominator: 4 } },
  simplificacion: { left: { numerator: 4, denominator: 6 }, right: { numerator: 2, denominator: 3 } },
};

export default function FraccionesSubSelect({ profile, onReset }) {
  const navigate = useNavigate();

  return (
    <PageShell>
      <ProfileBar profile={profile} onReset={onReset} />

      <div className="mt-6">
        <ClayButton variant="ghost" onClick={() => navigate("/dashboard")} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" strokeWidth={3} />
          Volver
        </ClayButton>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-2"
      >
        <div className="inline-flex items-center gap-3 rounded-full px-4 py-2 mb-4 bg-[#FCE7F3]">
          <span className="w-9 h-9 rounded-xl flex items-center justify-center font-heading text-2xl font-bold text-white bg-[#EC4899]">
            ½
          </span>
          <span className="font-bold text-base text-[#9D174D]">Fracciones</span>
        </div>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1C1C1E] mb-2">
          ¿Qué quieres practicar?
        </h2>
        <p className="text-base sm:text-lg text-[#5C5C60] mb-8 max-w-xl">
          Elige una operación con fracciones y luego un nivel.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6" data-testid="frac-sub-grid">
          {FRACTION_SUB_OPS.map((sub, i) => {
            const pv = PREVIEW_FRACTIONS[sub.id];
            return (
              <motion.button
                key={sub.id}
                onClick={() => navigate(`/practica/fracciones/${sub.id}`)}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08, type: "spring", bounce: 0.4 }}
                whileHover={{ y: -4 }}
                whileTap={{ scale: 0.97 }}
                className="clay-btn relative rounded-[2rem] p-5 sm:p-6 text-left bg-white border-4 border-b-[8px] active:border-b-4 overflow-hidden"
                style={{ borderColor: sub.colorLight, borderBottomColor: sub.color }}
                data-testid={`frac-sub-${sub.id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div
                    className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center font-heading text-3xl sm:text-4xl font-bold text-white"
                    style={{ backgroundColor: sub.color }}
                  >
                    {sub.symbol}
                  </div>
                  <div className="flex items-center gap-2">
                    <FractionVisual
                      numerator={pv.left.numerator}
                      denominator={pv.left.denominator}
                      size={40}
                      color={sub.color}
                      strokeColor={sub.borderDark}
                      emptyColor={sub.colorLight}
                    />
                    <span className="text-2xl font-heading font-bold" style={{ color: sub.color }}>
                      {sub.symbol}
                    </span>
                    <FractionVisual
                      numerator={pv.right.numerator}
                      denominator={pv.right.denominator}
                      size={40}
                      color={sub.color}
                      strokeColor={sub.borderDark}
                      emptyColor={sub.colorLight}
                    />
                  </div>
                </div>
                <h3 className="font-heading text-2xl sm:text-3xl font-bold text-[#1C1C1E]">
                  {sub.short}
                </h3>
                <p className="text-sm sm:text-base text-[#5C5C60]">{sub.description}</p>
              </motion.button>
            );
          })}
        </div>
      </motion.div>
    </PageShell>
  );
}
